import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import QRCode from "qrcode";
import { Shell } from "../components/Shell";
import { useAuth } from "../lib/auth-context";
import { auth as authApi, ApiError } from "../lib/api";
import type { TotpEnrollResponse } from "../lib/api";

type Mode = "idle" | "enrolling" | "disabling";

export function SecurityPage() {
  const { user, refresh } = useAuth();
  const [mode, setMode] = useState<Mode>("idle");
  const [enrollData, setEnrollData] = useState<TotpEnrollResponse | null>(null);
  const [qrSvg, setQrSvg] = useState<string>("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Render QR cuando llega el otpauth URI
  useEffect(() => {
    if (!enrollData) return;
    QRCode.toString(enrollData.otpauth_uri, {
      type: "svg",
      margin: 0,
      errorCorrectionLevel: "M",
      color: { dark: "#14161A", light: "#FFFFFF" },
      width: 220,
    })
      .then(setQrSvg)
      .catch(() => setQrSvg(""));
  }, [enrollData]);

  const startEnroll = async () => {
    setError(null);
    setBusy(true);
    try {
      const data = await authApi.totpEnroll();
      setEnrollData(data);
      setMode("enrolling");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("Ya tienes TOTP activado.");
      } else {
        setError("No se pudo iniciar el enrollment.");
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await authApi.totpVerifyEnroll(code);
      await refresh();
      setMode("idle");
      setEnrollData(null);
      setCode("");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Código incorrecto. Vuelve a intentarlo.");
      } else {
        setError("No se pudo verificar el código.");
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await authApi.totpDisable(password, code);
      await refresh();
      setMode("idle");
      setCode("");
      setPassword("");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Contraseña o código TOTP incorrecto.");
      } else {
        setError("No se pudo desactivar TOTP.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <section className="page">
        <div className="eyebrow">Cuenta · Seguridad</div>
        <h1>Seguridad</h1>
        <p className="lede">
          Gestiona la autenticación de dos factores (TOTP) de tu cuenta de
          super_admin. Recomendado para cuentas con rol <code>owner</code>.
        </p>

        <div className="card" style={{ maxWidth: 640 }}>
          <div className="card-inner">
            <div className="eyebrow">Autenticación en dos pasos</div>

            {mode === "idle" && (
              <>
                <h2 style={{ marginTop: 4 }}>
                  {user?.totp_enabled ? "Activado" : "Sin activar"}
                </h2>
                <p style={{ color: "var(--ink-3)", marginBottom: 24 }}>
                  {user?.totp_enabled
                    ? "Cada login requerirá un código de 6 dígitos generado por tu app autenticadora."
                    : "Sin 2FA, cualquiera con tu contraseña puede entrar a la plataforma."}
                </p>
                {error && <div className="alert err">{error}</div>}
                <button
                  className="cta"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (user?.totp_enabled) {
                      setError(null);
                      setMode("disabling");
                    } else {
                      void startEnroll();
                    }
                  }}
                  style={{ maxWidth: 260 }}
                >
                  <span className="cta-inner">
                    {busy && <span className="spinner" aria-hidden />}
                    {user?.totp_enabled ? "Desactivar TOTP" : "Activar TOTP"}
                  </span>
                </button>
              </>
            )}

            {mode === "enrolling" && enrollData && (
              <form onSubmit={confirmEnroll}>
                <h2 style={{ marginTop: 4 }}>Escanea el código</h2>
                <p style={{ color: "var(--ink-3)", marginBottom: 18 }}>
                  Usa Google Authenticator, Authy o 1Password.
                </p>

                <div
                  style={{
                    display: "flex",
                    gap: 32,
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      padding: 18,
                      background: "var(--paper)",
                      border: "1px solid var(--hairline-strong)",
                    }}
                    dangerouslySetInnerHTML={{ __html: qrSvg }}
                  />
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div className="eyebrow">o ingrésalo manual</div>
                    <code
                      style={{
                        display: "block",
                        fontFamily: "var(--font-mono)",
                        fontSize: 13,
                        wordBreak: "break-all",
                        background: "var(--bone)",
                        padding: 10,
                        border: "1px solid var(--hairline)",
                        marginTop: 8,
                      }}
                    >
                      {enrollData.secret_base32}
                    </code>
                    <p className="helper" style={{ marginTop: 14 }}>
                      Guarda este secreto en un lugar seguro como backup.
                    </p>
                  </div>
                </div>

                <div className="form-row" style={{ marginTop: 28 }}>
                  <label className="label" htmlFor="confirm-code">
                    Código generado por la app
                  </label>
                  <input
                    id="confirm-code"
                    className="input"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) =>
                      setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="123 456"
                    style={{
                      fontFamily: "var(--font-mono)",
                      letterSpacing: "0.4em",
                      fontSize: 18,
                      textAlign: "center",
                      maxWidth: 200,
                    }}
                    required
                    autoFocus
                  />
                </div>

                {error && <div className="alert err">{error}</div>}

                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    type="button"
                    className="logout-btn"
                    onClick={() => {
                      setMode("idle");
                      setEnrollData(null);
                      setCode("");
                      setError(null);
                    }}
                    disabled={busy}
                  >
                    Cancelar
                  </button>
                  <button
                    className="cta"
                    type="submit"
                    disabled={busy || code.length !== 6}
                    style={{ maxWidth: 260 }}
                  >
                    <span className="cta-inner">
                      {busy && <span className="spinner" aria-hidden />}
                      Activar
                    </span>
                  </button>
                </div>
              </form>
            )}

            {mode === "disabling" && (
              <form onSubmit={confirmDisable}>
                <h2 style={{ marginTop: 4 }}>Desactivar 2FA</h2>
                <p style={{ color: "var(--ink-3)", marginBottom: 18 }}>
                  Confirma tu contraseña y un código TOTP para desactivar.
                </p>

                <div className="form-row">
                  <label className="label">Contraseña</label>
                  <input
                    className="input"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                <div className="form-row">
                  <label className="label">Código TOTP actual</label>
                  <input
                    className="input"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) =>
                      setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="123 456"
                    style={{
                      fontFamily: "var(--font-mono)",
                      letterSpacing: "0.4em",
                      fontSize: 18,
                      textAlign: "center",
                      maxWidth: 200,
                    }}
                    required
                  />
                </div>

                {error && <div className="alert err">{error}</div>}

                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    type="button"
                    className="logout-btn"
                    onClick={() => {
                      setMode("idle");
                      setCode("");
                      setPassword("");
                      setError(null);
                    }}
                    disabled={busy}
                  >
                    Cancelar
                  </button>
                  <button
                    className="cta"
                    type="submit"
                    disabled={busy || !password || code.length !== 6}
                    style={{ maxWidth: 260 }}
                  >
                    <span className="cta-inner">
                      {busy && <span className="spinner" aria-hidden />}
                      Desactivar
                    </span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </Shell>
  );
}
